import { useTranslation } from 'react-i18next';
import React, { useRef } from 'react';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useShake } from '../../hooks/use-shake';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { SessionDetailModalTitle } from '../../components/order/session-detail-modal-title';
import { SessionDetailModalBody } from '../../components/order/session-detail-modal-body';
import { useFeature } from '../../hooks/use-feature';

function useSessionDetailModal() {
  const storage = useStorage();
  const { config, menuInfo, spot } = storage;
  const { swal } = useSwal({ storage });
  const { handleErrorOnModal } = useErrorHandler();
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const feature = useFeature();
  const { t } = useTranslation();

  const confirmButtonTextRef = useRef();
  const allOrdersRef = useRef();
  const addressRef = useRef();

  useShake({
    element: confirmButtonTextRef?.current?.parentElement,
  });

  const didOpen = () => {
    analyticsPageView('Session Detail');
  };

  async function prepare({ address, updateOrders }) {
    allOrdersRef.current = await updateOrders();
    addressRef.current = address;
  }

  async function show({ prepareOnPreConfirm, prepareOnPreDeny }) {
    const result = await swal.fire({
      title: <SessionDetailModalTitle />,
      html: (
        <SessionDetailModalBody
          address={addressRef.current}
          orders={allOrdersRef.current}
          config={config}
          feature={feature}
          menuInfo={menuInfo}
          spot={spot}
        />
      ),
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      didOpen,
      showConfirmButton: true,
      showDenyButton: true,
      showCancelButton: false,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      confirmButtonText: (
        <span ref={confirmButtonTextRef}>
          {t('payment-button.pay-and-send')}
        </span>
      ),
      denyButtonText: t('cancel'),
    });

    return result;
  }

  async function preConfirm({ prepareOnPreConfirm }) {
    try {
      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  async function preDeny({ prepareOnPreDeny }) {
    try {
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  return {
    prepareSessionDetailModal: prepare,
    showSessionDetailModal: show,
  };
}

export { useSessionDetailModal };
